import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Tooltip } from "@material-ui/core";
import { vendorInvoiceStyles } from "../style";
import { useGetAutoMatchingWorkordersForInvoiceQuery } from "../vendorInvoiceSlice";
import AILoader from "components/AILoader";
import { arrayToObject } from "utils/object-util";
import TrendingFlatIcon from '@material-ui/icons/TrendingFlat';
import CloseIcon from '@material-ui/icons/Close';
import CheckIcon from '@material-ui/icons/Check';
import { useEffect, useMemo, useState } from "react";
import EmptyPlaceholder from "components/EmptyPlaceholder/EmptyPlaceholder";
import { useGetQuickbooksMappingByTypeQuery } from "features/invoice/shared/QuickBooksMapping/QuickBooksMappingSlice";
import { useGetInvoiceAccountsQuery } from "features/invoice/loads/loadInvoiceSlice";
import { quickBooksMappingEntityTypes } from "features/invoice/shared/QuickBooksMapping/QuickBooksMappingConsts";

export const AutoMatchWOsToServices = ({
    services,
    setServices,
    invoiceToEdit,
    open,
    onClose,
    workOrdersList
}) => {
    const classes = vendorInvoiceStyles();
    let { data: matchingWorkOrders, error: matchingWorkOrdersError, isFetching: matchingWorkOrdersFetching, refetch: refetchMatchingWorkOrders } = useGetAutoMatchingWorkordersForInvoiceQuery({
        invoiceId: invoiceToEdit?.id,
        vehicleIds: invoiceToEdit?.vehicles?.map(v => v.vehicleId),
        invoiceDate: invoiceToEdit?.invoiceDate,
    }, { skip: !invoiceToEdit?.vehicles || !invoiceToEdit?.invoiceDate || !services?.length });
    let {
        data: mappedQbAccount,
        error: mappedQbAccountError,
        isFetching: isLoadingappedQbAccount
    } = useGetQuickbooksMappingByTypeQuery({ type: quickBooksMappingEntityTypes.ACTIVITY_ITEM });
    let { data: invoiceAccounts, error: accountsError, isFetching: accountsFetching } = useGetInvoiceAccountsQuery({});
    mappedQbAccount = mappedQbAccount?.data || [];
    invoiceAccounts = invoiceAccounts || [];

    const matchingWorkOrdersFiltered = useMemo(() => {
        return (matchingWorkOrders || []).filter(mw => mw.matchConfidence >= 0.4);
    }, [matchingWorkOrders]);

    useEffect(() => {
        if (open && !matchingWorkOrdersFetching) {
            refetchMatchingWorkOrders();
        }
    }, [open]);

    const matchingWorkOrdersLookup = useMemo(() => {
        return arrayToObject(matchingWorkOrdersFiltered, 'serviceId');
    }, [matchingWorkOrdersFiltered]);
    const workOrderListLookup = useMemo(() => arrayToObject(workOrdersList || [], 'workOrderId'), [workOrdersList]);
    const mappedQbAccountLookup = useMemo(() => arrayToObject(mappedQbAccount, 'entityId'), [mappedQbAccount]);
    const invoiceAccountsLookup = useMemo(() => arrayToObject(invoiceAccounts), [invoiceAccounts]);

    const [servicesToMatch, setServicesToMatch] = useState([]);

    useEffect(() => {
        let servicesWithMatches = services?.map(service => {
            const match = matchingWorkOrdersLookup[service.id];
            const quickbooksMapping = match && workOrderListLookup?.[match?.matchedWorkOrderId]?.activityItemId ?
                mappedQbAccountLookup[workOrderListLookup?.[match?.matchedWorkOrderId]?.activityItemId] : null;
            return {
                ...service,
                matchedWorkOrderId: match ? match.matchedWorkOrderId : null,
                matchedQuickbooksId: quickbooksMapping ? quickbooksMapping.quickbooksId : null,
            };
        })?.filter(s => s.matchedWorkOrderId != null);
        setServicesToMatch(servicesWithMatches ?? []);
    }, [matchingWorkOrdersFiltered]);

    const onChange = (value, index) => {
        const updatedServices = [...servicesToMatch];
        updatedServices[index] = { ...updatedServices[index], acceptMatch: value };
        setServicesToMatch(updatedServices);
    };

    const onAcceptAll = (value) => {
        const updatedServices = servicesToMatch.map(s => ({ ...s, acceptMatch: value }));
        setServicesToMatch(updatedServices);
    };

    const onSave = () => {
        const updatedServices = services?.map(service => {
            const serviceToMatch = servicesToMatch.find(s => s.id === service.id);
            if (serviceToMatch && serviceToMatch.acceptMatch) {
                return {
                    ...service,
                    workOrderId: serviceToMatch.matchedWorkOrderId,
                    qbAccountId: serviceToMatch.matchedQuickbooksId ?? service.qbAccountId,
                };
            }
            return service;
        });
        setServices(updatedServices);
        onClose();
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle>Auto-Match Work Orders to Services</DialogTitle>
                <DialogContent>
                    {matchingWorkOrdersFetching && <AILoader />}
                    {!matchingWorkOrdersFetching && servicesToMatch?.length === 0 && <EmptyPlaceholder text="No matching work orders found for the services in this invoice." />}
                    <div>
                        {servicesToMatch && servicesToMatch.length > 0 && !matchingWorkOrdersFetching &&
                            <>
                                <div className={classes.subTitle}>
                                    <b>Please review the following matches and accept or reject them:</b>
                                    <div>
                                        <Button
                                            variant="outlined"
                                            startIcon={<CheckIcon className={classes.acceptIcon} />}
                                            className={classes.acceptAll}
                                            onClick={() => onAcceptAll(true)}>
                                            Accept All
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            startIcon={<CloseIcon className={classes.rejectIcon} />}
                                            className={classes.rejectAll}
                                            onClick={() => onAcceptAll(false)}>
                                            Reject All
                                        </Button>
                                    </div>
                                </div>
                                {servicesToMatch?.map((service, idx) => (
                                    <div key={idx} className={`${classes.matchContainer} ${service?.acceptMatch ? classes.acceptedMatch : ''}`}>
                                        <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                                            <Grid item xs={3}>
                                                <div><b className={classes.matchTitle}>Service:</b></div>
                                                <div className={classes.description}>{service.description}</div>
                                            </Grid>
                                            <Grid item >
                                                <TrendingFlatIcon className={classes.arrowIcon} />
                                            </Grid>
                                            <Grid item xs={3}>
                                                <div><b className={classes.matchTitle}>Recommended Work Order:</b></div>
                                                <div className={classes.matchDescription}>{`${workOrderListLookup?.[service?.matchedWorkOrderId]?.activityName} - ${workOrderListLookup?.[service?.matchedWorkOrderId]?.formattedDate}`}</div>
                                            </Grid>
                                            {service?.matchedQuickbooksId &&
                                                <Grid item xs={3}>
                                                    <div><b className={classes.matchTitle}>Mapped QuickBooks Account:</b></div>
                                                    <div className={classes.description}>{invoiceAccountsLookup?.[service?.matchedQuickbooksId]?.name}</div>
                                                </Grid>}
                                            <Grid item xs={1} className={classes.actions}>
                                                <Tooltip title="Accept Match">
                                                    <IconButton onClick={() => onChange(true, idx)}>
                                                        <CheckIcon className={classes.acceptIcon} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Reject Match">
                                                    <IconButton onClick={() => onChange(false, idx)}>
                                                        <CloseIcon className={classes.rejectIcon} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Grid>
                                        </Grid>
                                    </div>
                                ))}
                            </>
                        }
                    </div>
                </DialogContent>
                <DialogActions >
                    <Button
                        onClick={onSave}
                        variant="contained"
                        disabled={servicesToMatch?.filter(s => s.acceptMatch)?.length === 0}
                        color="primary">
                        Apply
                    </Button>
                </DialogActions>
            </Dialog>

        </>
    );
};
